<template>
  <vs-wrapper space="small">
    <ad-hoc-pool-stage-creator
      v-for="stage in props.adHocPool.stages"
      :key="stage.stageNumber"
      :stage="stage"
      :selectable-service-providers="selectableServiceProviders(stage.stageNumber)"
      @update-stage="updateStage"
      @remove-stage="removeStage"
    >
    </ad-hoc-pool-stage-creator>
    <v-row class="pt-1">
      <v-col v-if="stageCount > 1" align-self="center" class="shrink">
        <v-icon> mdi-timer-outline </v-icon>
      </v-col>
      <v-col v-if="stageCount > 1" class="pr-1">
        <vs-text-input
          data-test="delay-input"
          type="number"
          min-value="0"
          free-form
          label="Delay"
          :value="props.adHocPool.delay"
          required
          :rules="[(delay) => delay > 43200 && 'Delay must be less than 43200 minutes (30 days)']"
          @input="updateDelay"
        />
      </v-col>
      <v-col v-if="stageCount > 1" class="shrink pl-0" align-self="center">
        <vs-text type="subdued">minutes</vs-text>
      </v-col>
      <v-spacer></v-spacer>
      <v-col class="shrink">
        <vs-button
          data-test="add-stage-button"
          label="Add stage"
          type="secondary"
          icon="workspaces"
          :disabled="disableAddStageButton"
          small
          @click="addStage"
        />
      </v-col>
    </v-row>
  </vs-wrapper>
</template>

<script setup lang="ts">
import VsButton from '@/components/vision/VsButton.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import AdHocPoolStageCreator from '@/components/tickets/AdHocPoolStageCreator.vue'
import { computed, ref } from 'vue'
import { useEligibleServiceProvidersQuery } from '../../api/pools'

export type AdHocPoolStage = {
  stageNumber: number
  recipients: string[]
}
export type AdHocPool = {
  stages: AdHocPoolStage[]
  delay: string | null
}

const emits = defineEmits<{
  (e: 'update-stage', stage: AdHocPoolStage)
  (e: 'remove-stage', stageNumber: number)
  (e: 'add-stage', stageNumber: number)
  (e: 'update-delay', delay: string)
}>()

const props = defineProps<{
  adHocPool: AdHocPool
  resourceTypeId: number
  hubId: string
}>()

const { data: eligibleServiceProviders } = useEligibleServiceProvidersQuery(
  ref(props.hubId),
  ref(props.resourceTypeId),
)
const selectableServiceProviders = (stageNumber) => {
  if (eligibleServiceProviders && eligibleServiceProviders.value) {
    const selectedServiceProviderIds = props.adHocPool.stages
      .filter((s) => s.stageNumber !== stageNumber)
      .map((s) => s.recipients)
      .flat()

    return eligibleServiceProviders.value.filter(
      (sp) => !selectedServiceProviderIds.includes(sp.id),
    )
  } else {
    return []
  }
}

const stageCount = computed<number>(() => {
  return props.adHocPool.stages.length
})

const disableAddStageButton = computed<boolean>(() => {
  const totalRecipients = props.adHocPool.stages.reduce(
    (acc, stage) => acc + stage.recipients.length,
    0,
  )
  return eligibleServiceProviders?.value?.length === totalRecipients
})

const updateDelay = (value: string) => {
  emits('update-delay', value)
}
const addStage = () => {
  emits('add-stage', stageCount.value + 1)
}
const updateStage = (value: AdHocPoolStage) => {
  emits('update-stage', value)
}
const removeStage = (stageNumber) => {
  emits('remove-stage', stageNumber)
}
</script>
